import { useCallback, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Button,
  Card,
  RECORD_RAIL_WIDTH,
  ResponsiveSplit,
} from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import {
  useDeal,
  useUpdateDealStage,
} from "@/hooks/deals/useDeals";
import { useToast } from "@/components/ui/Toast";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { ApiError } from "@/api/client";
import { useIntakeChecklist, useDocumentRequests } from "@/hooks/deals/useDealDetail";
import { DealDetailHeader } from "@/components/deals/DealDetailHeader";
import { RequestDocumentsButton } from "@/components/Collection/RequestDocumentsButton";
import { DealDeclineBanner } from "@/components/deals/DealDeclineBanner";
import { WithdrawnOrDeclinedDealBanner } from "@/components/deals/WithdrawnOrDeclinedDealBanner";
import { DealOverviewTab } from "@/components/deals/tabs/DealOverviewTab";
import { DealIntakeTab } from "@/components/deals/tabs/DealIntakeTab";
import { DealPreApprovalTab } from "@/components/deals/tabs/DealPreApprovalTab";
import { dealLabel } from "@/components/deals/dealFormat";
import type { PrimaryActionId } from "@/components/deals/getPrimaryAction";
import type { DealListItemDto } from "@/types/deals";
import { DocumentPanel } from "@/pages/documents/components/DocumentPanel";
import { TaskListMini } from "@/pages/tasks/components/TaskListMini";
import { AppointmentListMini } from "@/pages/calendar/components/AppointmentListMini";
import { CommunicationsTabPanel } from "@/pages/communications/components/CommunicationsTabPanel";
import { NotesTabPanel } from "@/pages/notes/components/NotesTabPanel";
import { ActivityTabPanel } from "@/pages/activity/components/ActivityTabPanel";
import { RelatedConversationsPanel } from "@/components/messaging/RelatedConversationsPanel";
import { OverviewActivityPreview } from "@/components/activity/OverviewActivityPreview";
import { useOpenTaskCountByRelated } from "@/pages/tasks/hooks/useWorkTasks";
import { useCommunicationsRecentCount } from "@/pages/communications/hooks/useCommunications";
import { useNotesRecentCount } from "@/pages/notes/hooks/useNotes";
import { useActivityRecentCount } from "@/pages/activity/hooks/useActivityByOwner";
import {
  CROSS_CUTTING_TAB_ICONS,
  EntityTabBar,
  type EntityTabItem,
} from "@/components/layout/EntityTabBar";
import {
  ClipboardCheck,
  FileInput,
  Gavel,
  Handshake,
  Landmark,
  ShieldCheck,
  TrendingUp,
} from "lucide-react";
import { UnderwritingTabContent } from "@/components/DealFlow/UnderwritingTab";
import {
  BusinessAnalysisTabContent,
  FinancialAnalysisTabContent,
  LegalAnalysisTabContent,
} from "@/components/DealFlow/AnalystTabs";
import {
  ClosingTabContent,
  ClosedDealBanner,
} from "@/components/DealFlow/ClosingTab";
import { TabPlaceholderCard } from "@/components/DealFlow/common/TabPlaceholderCard";
import type { DealPipelineStage } from "@/types/deals";

// Layout Compliance v1.0 PR 7 — Deal pipeline page is the third entity
// rollout. Per Layout Standard §3 the entity-specific tabs (Intake /
// Pre-Approval) keep their existing positions; the cross-cutting block
// (Documents / Tasks / Calendar / Messages / Communications / Notes /
// Activity) is appended in canonical order. Files renames to Documents and is
// wired to the shared <DocumentPanel> per Document Access Standard v1.1.
// The deal detail page carries a 15-tab strip (Entity Detail Page Layout
// v1.1 §3.1). Tabs 4-8 (Underwriting / Financial Analysis / Legal Analysis
// / Business Analysis / Closing) render a placeholder card when the deal is
// earlier than their gating stage, and switch to the live surface once the
// gate clears.
type TabId =
  | "overview"
  | "intake"
  | "pre-approval"
  | "underwriting"
  | "financial-analysis"
  | "legal-analysis"
  | "business-analysis"
  | "closing"
  | "documents"
  | "tasks"
  | "calendar"
  | "messages"
  | "communications"
  | "notes"
  | "activity";

const TABS: readonly TabId[] = [
  "overview",
  "intake",
  "pre-approval",
  "underwriting",
  "financial-analysis",
  "legal-analysis",
  "business-analysis",
  "closing",
  "documents",
  "tasks",
  "calendar",
  "messages",
  "communications",
  "notes",
  "activity",
] as const;

const TAB_ITEMS: ReadonlyArray<EntityTabItem<TabId>> = [
  { key: "overview", label: "Overview", icon: CROSS_CUTTING_TAB_ICONS.overview },
  { key: "intake", label: "Intake", icon: FileInput },
  { key: "pre-approval", label: "Pre-Approval", icon: ClipboardCheck },
  { key: "underwriting", label: "Underwriting", icon: ShieldCheck },
  { key: "financial-analysis", label: "Financial Analysis", icon: TrendingUp },
  { key: "legal-analysis", label: "Legal Analysis", icon: Gavel },
  { key: "business-analysis", label: "Business Analysis", icon: Landmark },
  { key: "closing", label: "Closing", icon: Handshake },
  { key: "documents", label: "Documents", icon: CROSS_CUTTING_TAB_ICONS.documents },
  { key: "tasks", label: "Tasks", icon: CROSS_CUTTING_TAB_ICONS.tasks },
  { key: "calendar", label: "Calendar", icon: CROSS_CUTTING_TAB_ICONS.calendar },
  { key: "messages", label: "Messages", icon: CROSS_CUTTING_TAB_ICONS.messages },
  { key: "communications", label: "Communications", icon: CROSS_CUTTING_TAB_ICONS.communications },
  { key: "notes", label: "Notes", icon: CROSS_CUTTING_TAB_ICONS.notes },
  { key: "activity", label: "Activity", icon: CROSS_CUTTING_TAB_ICONS.activity },
] as const;

// Helpers — ordinal index in the canonical pipeline-stage progression.
// "Closing" / "Closed" are downstream of "Underwriting".
const STAGE_ORDER: Record<DealPipelineStage, number> = {
  Intake: 0,
  PreApproval: 1,
  Underwriting: 2,
  Closing: 3,
  Closed: 4,
  Declined: 0,
  Withdrawn: 0,
};
function stageGteUnderwriting(stage: DealPipelineStage): boolean {
  return STAGE_ORDER[stage] >= STAGE_ORDER.Underwriting;
}
function stageGteClosing(stage: DealPipelineStage): boolean {
  return STAGE_ORDER[stage] >= STAGE_ORDER.Closing;
}

function isTabId(value: string | null): value is TabId {
  return value !== null && (TABS as readonly string[]).includes(value);
}

const PRIMARY_ACTION_TARGET_TAB: Record<PrimaryActionId, TabId> = {
  "mark-review-complete": "intake",
  "mark-ready-for-assignment": "intake",
  "advance-to-preapproval": "pre-approval",
  "open-applications": "pre-approval",
  "open-letters": "pre-approval",
  "open-document-requests": "pre-approval",
  "send-to-underwriting": "pre-approval",
  "advance-to-underwriting": "pre-approval",
  clone: "overview",
};

/**
 * Spec §5.5 / Layout Standard §3 — Deal Detail page at /deal-flow/deals/{dealId}.
 * Renders the 15-tab workspace with URL persistence via
 * `?tab={slug}` (Layout Standard §6.2). Numeric keys 1–9 jump tabs when
 * focus is outside form fields.
 */
export function DealDetailPage() {
  const { dealId } = useParams<{ dealId: string }>();
  const navigate = useNavigate();
  const auth = useAuth();
  const dealQuery = useDeal(dealId);
  const deal = dealQuery.data;

  // Breadcrumb: Deals › {Deal #}. The "deals" URL segment and the raw
  // GUID are dropped in favour of the human-readable deal number.
  useBreadcrumbs(
    deal
      ? [
          { label: "Deals", href: "/deal-flow/pipeline" },
          { label: deal.dealNumber },
        ]
      : [{ label: "Deals", href: "/deal-flow/pipeline" }],
  );

  const [params, setParams] = useSearchParams();
  const tabParam = params.get("tab");
  const tab: TabId = isTabId(tabParam) ? tabParam : "overview";

  const setTab = useCallback(
    (next: TabId) => {
      const updated = new URLSearchParams(params);
      if (next === "overview") updated.delete("tab");
      else updated.set("tab", next);
      setParams(updated, { replace: true });
    },
    [params, setParams],
  );

  // Numeric shortcuts: 1-9 jump to the matching tab when focus is not in
  // an input. Mirrors the Pipeline shortcuts module's typing-target check.
  useEffect(() => {
    function isTypingTarget(t: EventTarget | null): boolean {
      if (!(t instanceof HTMLElement)) return false;
      if (t.isContentEditable) return true;
      const tag = t.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
    }
    function handler(e: KeyboardEvent) {
      if (isTypingTarget(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const idx = Number(e.key);
      if (Number.isInteger(idx) && idx >= 1 && idx <= TABS.length) {
        e.preventDefault();
        setTab(TABS[idx - 1]);
      }
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [setTab]);

  // Tab badges (Layout Standard §5). Entity-specific tabs (Intake /
  // Pre-Approval) source their counts from the deal-domain hooks; the
  // cross-cutting tabs use the central by-owner counters so badges
  // reflect the same data the canonical panels render.
  const checklist = useIntakeChecklist(deal?.id);
  const docRequests = useDocumentRequests(deal?.id);
  const openTaskCountQuery = useOpenTaskCountByRelated("Deal", deal?.id ?? undefined);
  const openTaskCount = openTaskCountQuery.data ?? 0;
  const commsRecent = useCommunicationsRecentCount("deal", deal?.id ?? "", 30);
  const notesRecent = useNotesRecentCount("deal", deal?.id ?? "", 7);
  const activityRecent = useActivityRecentCount("deal", deal?.id ?? "", 7);

  const advanceStage = useUpdateDealStage(deal?.id ?? "");
  const toast = useToast();

  const onPrimaryAction = useCallback(
    (id: PrimaryActionId) => {
      if (!deal) return;
      if (id === "clone") {
        // Header handles clone via the overflow menu — this branch is for
        // clarity in the action enum but never fires from the primary
        // button because the header maps "clone" to its own handler.
        return;
      }
      if (id === "advance-to-preapproval") {
        // Inconsistent-state recovery: B1 was assigned but the stage never
        // advanced. Fire the standard PATCH /api/deals/{id}/stage call.
        advanceStage
          .mutateAsync({
            targetStage: "PreApproval",
            targetSubState: "Applications",
            reason: "Manual advance after B1 assignment.",
          })
          .then(() => {
            toast.success("Deal advanced to Pre-Approval.");
            setTab("pre-approval");
          })
          .catch((err) => {
            toast.error(
              err instanceof ApiError && err.errors.length
                ? err.errors.join("; ")
                : err instanceof Error
                  ? err.message
                  : "Could not advance the deal.",
            );
          });
        return;
      }
      if (id === "advance-to-underwriting") {
        // Inconsistent-state recovery: A1 was assigned but the stage never
        // advanced past PreApproval/ReadyToSubmit. Mirrors the B1 fallback
        // above.
        advanceStage
          .mutateAsync({
            targetStage: "Underwriting",
            targetSubState: "AssignedToA1",
            reason: "Manual advance after A1 assignment.",
          })
          .then(() => {
            toast.success("Deal advanced to Underwriting.");
            setTab("pre-approval");
          })
          .catch((err) => {
            toast.error(
              err instanceof ApiError && err.errors.length
                ? err.errors.join("; ")
                : err instanceof Error
                  ? err.message
                  : "Could not advance the deal.",
            );
          });
        return;
      }
      // Pre-Approval milestones — every page-section CTA simply swaps to
      // the Pre-Approval tab. The sub-state itself moves automatically
      // when the LA advances the underlying milestones on the page.
      setTab(PRIMARY_ACTION_TARGET_TAB[id]);
    },
    [deal, setTab, advanceStage, toast],
  );

  if (dealQuery.isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">Loading deal…</p>
      </Card>
    );
  }

  if (!deal) {
    return (
      <Card>
        <h2 className="font-display font-bold text-h2">Deal not found</h2>
        <p className="text-small text-gray-mid">
          The deal you requested was not found or you don&apos;t have permission to view it.
        </p>
        <button
          type="button"
          className="mt-space-3 text-gold-shadow hover:underline text-small"
          onClick={() => navigate("/pipeline")}
        >
          Back to pipeline
        </button>
      </Card>
    );
  }

  const tabBadge = (key: TabId): string | number | null => {
    switch (key) {
      case "intake": {
        const items = checklist.data?.items;
        if (!Array.isArray(items)) return null;
        const open = items.filter(
          (i) => i.required && i.status !== "Complete" && i.status !== "NotApplicable",
        ).length;
        return open > 0 ? open : null;
      }
      case "pre-approval": {
        if (!Array.isArray(docRequests.data)) return null;
        const open = docRequests.data.filter(
          (r) => r.status === "Received" || r.status === "UnderReview",
        ).length;
        return open > 0 ? open : null;
      }
      case "tasks":
        return openTaskCount > 0 ? openTaskCount : null;
      case "communications":
        return commsRecent.data && commsRecent.data > 0 ? commsRecent.data : null;
      case "notes":
        return notesRecent.data && notesRecent.data > 0 ? notesRecent.data : null;
      case "activity":
        return activityRecent.data && activityRecent.data > 0 ? activityRecent.data : null;
      default:
        return null;
    }
  };

  const dealName = dealLabel(deal as unknown as DealListItemDto);

  return (
    <div className="flex flex-col gap-space-3">
      <DealDetailHeader deal={deal} onPrimaryAction={onPrimaryAction} />

      <div className="flex justify-end gap-space-2">
        {(auth.hasPermission("BackgroundCheck.Read") ||
          auth.hasPermission("BackgroundCheck.RunKyb") ||
          auth.hasPermission("BackgroundCheck.RunConsumer") ||
          auth.hasPermission("BackgroundCheck.RunOnline") ||
          auth.hasPermission("BackgroundCheck.RunLienSearch")) && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => navigate(`/background-checks?dealId=${deal.id}`)}
          >
            <ShieldCheck size={16} aria-hidden="true" /> Run Background Check
          </Button>
        )}
        <RequestDocumentsButton
          ownerEntityType="Deal"
          ownerEntityId={deal.id}
          ownerDisplayName={dealName}
          size="sm"
        />
      </div>

      {/* Layout Standard §6.1 — sticky tab bar below the entity header.
          Entity-specific tabs (Intake / Pre-Approval) keep their existing
          positions per §3; the cross-cutting block is appended after
          Closing in canonical order. The shared <EntityTabBar>
          primitive handles overflow at 1280–1440px viewports by
          collapsing inactive labels to icon-only. */}
      <EntityTabBar
        ariaLabel="Deal sections"
        testIdPrefix="deal"
        items={TAB_ITEMS.map((it) => ({ ...it, badge: tabBadge(it.key) }))}
        value={tab}
        onChange={setTab}
      />

      {deal.stage === "Closed" && tab !== "closing" && (
        <ClosedDealBanner deal={deal} />
      )}

      {/* Decline workflow — yellow proposal banner / red declined banner. */}
      <DealDeclineBanner dealId={deal.id} declineStatus={deal.declineStatus} />

      {/* Withdrawn or stage-Declined (not ratified): read-only except ADMIN. */}
      <WithdrawnOrDeclinedDealBanner deal={deal} />

      <div className="pt-space-4">
        {/* Container-driven — Table & Tile Standard §12. A `lg:` breakpoint
            asks the WINDOW how wide this box is, which is the one thing the
            window cannot answer once the box is a tab body, a drawer or a
            split pane. */}
        {tab === "overview" && (
          <ResponsiveSplit
            gap="md"
            mainMinWidth="34rem"
            railWidth={RECORD_RAIL_WIDTH}
            railGrows={false}
            main={<DealOverviewTab deal={deal} />}
            /* Layout Standard §4.1 — right-rail Recent Activity preview;
               full feed lives on the Activity tab. */
            rail={
              <OverviewActivityPreview
                ownerType="deal"
                ownerId={deal.id}
                detailPath={`/pipeline/${deal.id}`}
              />
            }
          />
        )}
        {tab === "intake" && <DealIntakeTab deal={deal} />}
        {tab === "pre-approval" && <DealPreApprovalTab deal={deal} />}
        {tab === "underwriting" &&
          (stageGteUnderwriting(deal.stage) ? (
            <UnderwritingTabContent deal={deal} />
          ) : (
            <TabPlaceholderCard
              title="Available once the deal reaches the Underwriting stage"
              body={`This tab activates when the deal advances to Underwriting.\n\nCurrent stage: ${deal.stage}.\n\nTabs accessible now: Overview, Intake, Pre-Approval, Documents, Tasks, Calendar, Messages, Communications, Notes, Activity.`}
            />
          ))}
        {tab === "financial-analysis" && (
          <FinancialAnalysisTabContent deal={deal} />
        )}
        {tab === "legal-analysis" && <LegalAnalysisTabContent deal={deal} />}
        {tab === "business-analysis" && (
          <BusinessAnalysisTabContent deal={deal} />
        )}
        {tab === "closing" &&
          (stageGteClosing(deal.stage) ? (
            <ClosingTabContent deal={deal} />
          ) : (
            <TabPlaceholderCard
              title="Available once the deal reaches the Closing stage"
              body={`This tab activates when the deal advances to Closing.\n\nCurrent stage: ${deal.stage}.`}
            />
          ))}
        {tab === "documents" && (
          <DocumentPanel
            ownerType="Deal"
            ownerId={deal.id}
            rootName={dealName}
            initialFilter={params.get("docFilter") ?? undefined}
          />
        )}
        {tab === "tasks" && (
          <TaskListMini
            relatedEntityType="Deal"
            relatedEntityId={deal.id}
            addButtonLabel="+ Add task linked to this Deal"
          />
        )}
        {tab === "calendar" && (
          <AppointmentListMini
            relatedEntityType="Deal"
            relatedEntityId={deal.id}
            entityLabel={dealName}
          />
        )}
        {tab === "messages" && (
          <RelatedConversationsPanel
            resourceType="deal"
            resourceId={deal.id}
            entityLabel={dealName}
          />
        )}
        {tab === "communications" && (
          <CommunicationsTabPanel
            ownerType="deal"
            ownerId={deal.id}
            ownerLabel={dealName}
          />
        )}
        {tab === "notes" && <NotesTabPanel ownerType="deal" ownerId={deal.id} />}
        {tab === "activity" && <ActivityTabPanel ownerType="deal" ownerId={deal.id} />}
      </div>
    </div>
  );
}
